/**
 * Master Data Management Service
 * Phase 1 Sprint 3: Real DB persistence for Stores, Products (SKUs), and Employees
 *
 * This service replaces mock data with actual Supabase database operations.
 * All queries are automatically scoped by tenant via RLS policies.
 */

import { supabase } from './supabase';

// ============================================
// TYPE DEFINITIONS
// ============================================

export interface Store {
  id: string;
  tenant_id: string;
  code: string;
  name: string;
  address?: string;
  city?: string;
  region?: string;
  country?: string;
  phone?: string;
  email?: string;
  manager_name?: string;
  format?: string;
  status: 'active' | 'inactive' | 'renovating' | 'closed';
  opening_date?: string;
  timezone?: string;
  latitude?: number;
  longitude?: number;
  square_meters?: number;
  employee_count?: number;
  created_at: string;
  updated_at: string;
}

export interface StoreCreateInput {
  code: string;
  name: string;
  address?: string;
  city?: string;
  region?: string;
  country?: string;
  phone?: string;
  email?: string;
  manager_name?: string;
  format?: string;
  status?: Store['status'];
  opening_date?: string;
  timezone?: string;
  latitude?: number;
  longitude?: number;
  square_meters?: number;
}

export interface StoreUpdateInput extends Partial<StoreCreateInput> {}

export interface Product {
  id: string;
  tenant_id: string;
  sku: string;
  barcode?: string;
  name: string;
  description?: string;
  category?: string;
  subcategory?: string;
  brand?: string;
  unit_cost?: number;
  unit_price?: number;
  currency_code?: string;
  unit_of_measure?: string;
  weight_kg?: number;
  is_active: boolean;
  is_high_risk?: boolean;
  risk_category?: 'low' | 'medium' | 'high' | 'critical';
  shrinkage_rate?: number;
  reorder_point?: number;
  reorder_quantity?: number;
  supplier_id?: string;
  // LP-critical: barcode identification
  gtin?: string;
  ean?: string;
  // LP-critical: catch-weight fraud detection
  catch_weight?: boolean;
  nominal_weight_kg?: number;
  // LP-critical: receiving pack verification
  pack_size?: number;
  case_size?: number;
  // LP-critical: shrinkage valuation
  last_purchase_cost?: number;
  created_at: string;
  updated_at: string;
}

export interface ProductCreateInput {
  sku: string;
  barcode?: string;
  name: string;
  description?: string;
  category?: string;
  subcategory?: string;
  brand?: string;
  unit_cost?: number;
  unit_price?: number;
  currency_code?: string;
  unit_of_measure?: string;
  weight_kg?: number;
  is_active?: boolean;
  is_high_risk?: boolean;
  risk_category?: Product['risk_category'];
  shrinkage_rate?: number;
  reorder_point?: number;
  reorder_quantity?: number;
  supplier_id?: string;
  gtin?: string;
  ean?: string;
  catch_weight?: boolean;
  nominal_weight_kg?: number;
  pack_size?: number;
  case_size?: number;
  last_purchase_cost?: number;
}

export interface ProductUpdateInput extends Partial<ProductCreateInput> {}

export type EmployeeStatus = 'ACTIVE' | 'INACTIVE' | 'TERMINATED' | 'ON_LEAVE';

export interface Employee {
  id: string;
  tenant_id: string;
  store_id?: string;
  user_id?: string;
  employee_code?: string;
  first_name: string;
  last_name: string;
  full_name?: string;
  email?: string;
  position?: string;
  department?: string;
  hire_date?: string;
  termination_date?: string;
  status: EmployeeStatus;
  risk_score?: number;
  created_at: string;
  updated_at: string;
  // Joined data
  store?: Pick<Store, 'id' | 'name' | 'code'>;
}

export interface EmployeeCreateInput {
  store_id?: string;
  user_id?: string;
  employee_code?: string;
  first_name: string;
  last_name: string;
  full_name?: string;
  email?: string;
  position?: string;
  department?: string;
  hire_date?: string;
  status?: EmployeeStatus;
}

export interface EmployeeUpdateInput extends Partial<EmployeeCreateInput> {
  termination_date?: string;
  risk_score?: number;
}

export interface PaginationParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface FilterParams {
  search?: string;
  status?: string;
  store_id?: string;
  category?: string;
  region?: string;
}

export interface PaginatedResult<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// ============================================
// STORE SERVICE
// ============================================

export const storeService = {
  /**
   * Get all stores for the current tenant with pagination
   */
  async getStores(
    pagination: PaginationParams = {},
    filters: FilterParams = {}
  ): Promise<PaginatedResult<Store>> {
    const { page = 1, pageSize = 20, sortBy = 'name', sortOrder = 'asc' } = pagination;
    const { search, status, region } = filters;

    let query = supabase
      .from('stores')
      .select('*', { count: 'exact' });

    // Apply filters
    if (search) {
      query = query.or(`name.ilike.%${search}%,code.ilike.%${search}%,city.ilike.%${search}%`);
    }
    if (status) {
      query = query.eq('status', status);
    }
    if (region) {
      query = query.eq('region', region);
    }

    // Apply sorting
    query = query.order(sortBy, { ascending: sortOrder === 'asc' });

    // Apply pagination
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    query = query.range(from, to);

    const { data, error, count } = await query;

    if (error) {
      console.error('Error fetching stores:', error);
      throw new Error(`Failed to fetch stores: ${error.message}`);
    }

    return {
      data: data || [],
      total: count || 0,
      page,
      pageSize,
      totalPages: Math.ceil((count || 0) / pageSize),
    };
  },

  /**
   * Get a single store by ID
   */
  async getStore(id: string): Promise<Store | null> {
    const { data, error } = await supabase
      .from('stores')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // Not found
      throw new Error(`Failed to fetch store: ${error.message}`);
    }

    return data;
  },

  /**
   * Get store by code
   */
  async getStoreByCode(code: string): Promise<Store | null> {
    const { data, error } = await supabase
      .from('stores')
      .select('*')
      .eq('code', code)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw new Error(`Failed to fetch store: ${error.message}`);
    }

    return data;
  },

  /**
   * Create a new store
   */
  async createStore(input: StoreCreateInput, tenantId: string): Promise<Store> {
    const { data, error } = await supabase
      .from('stores')
      .insert({
        ...input,
        tenant_id: tenantId,
        status: input.status || 'active',
      })
      .select()
      .single();

    if (error) {
      if (error.code === '23505') {
        throw new Error('A store with this code already exists');
      }
      throw new Error(`Failed to create store: ${error.message}`);
    }

    return data;
  },

  /**
   * Update an existing store
   */
  async updateStore(id: string, input: StoreUpdateInput): Promise<Store> {
    const { data, error } = await supabase
      .from('stores')
      .update({
        ...input,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update store: ${error.message}`);
    }

    return data;
  },

  /**
   * Delete a store (soft delete by setting status to 'closed')
   */
  async deleteStore(id: string, hardDelete = false): Promise<boolean> {
    if (hardDelete) {
      const { error } = await supabase
        .from('stores')
        .delete()
        .eq('id', id);

      if (error) {
        throw new Error(`Failed to delete store: ${error.message}`);
      }
    } else {
      const { error } = await supabase
        .from('stores')
        .update({ status: 'closed', updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) {
        throw new Error(`Failed to close store: ${error.message}`);
      }
    }

    return true;
  },

  /**
   * Get store count by status
   */
  async getStoreStats(): Promise<{ total: number; active: number; inactive: number; closed: number }> {
    const { data, error } = await supabase
      .from('stores')
      .select('status');

    if (error) {
      throw new Error(`Failed to fetch store stats: ${error.message}`);
    }

    const stats = {
      total: data?.length || 0,
      active: data?.filter(s => s.status === 'active').length || 0,
      inactive: data?.filter(s => s.status === 'inactive').length || 0,
      closed: data?.filter(s => s.status === 'closed').length || 0,
    };

    return stats;
  },
};

// ============================================
// PRODUCT SERVICE
// ============================================

export const productService = {
  /**
   * Get all products with pagination and filtering
   */
  async getProducts(
    pagination: PaginationParams = {},
    filters: FilterParams = {}
  ): Promise<PaginatedResult<Product>> {
    const { page = 1, pageSize = 50, sortBy = 'name', sortOrder = 'asc' } = pagination;
    const { search, category, status } = filters;

    let query = supabase
      .from('products')
      .select('*', { count: 'exact' });

    // Apply filters
    if (search) {
      query = query.or(`name.ilike.%${search}%,sku.ilike.%${search}%,barcode.ilike.%${search}%,brand.ilike.%${search}%`);
    }
    if (category) {
      query = query.eq('category', category);
    }
    if (status === 'active') {
      query = query.eq('is_active', true);
    } else if (status === 'inactive') {
      query = query.eq('is_active', false);
    }

    // Apply sorting
    query = query.order(sortBy, { ascending: sortOrder === 'asc' });

    // Apply pagination
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    query = query.range(from, to);

    const { data, error, count } = await query;

    if (error) {
      console.error('Error fetching products:', error);
      throw new Error(`Failed to fetch products: ${error.message}`);
    }

    return {
      data: data || [],
      total: count || 0,
      page,
      pageSize,
      totalPages: Math.ceil((count || 0) / pageSize),
    };
  },

  /**
   * Get a single product by ID
   */
  async getProduct(id: string): Promise<Product | null> {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw new Error(`Failed to fetch product: ${error.message}`);
    }

    return data;
  },

  /**
   * Get product by SKU
   */
  async getProductBySku(sku: string): Promise<Product | null> {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('sku', sku)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw new Error(`Failed to fetch product: ${error.message}`);
    }

    return data;
  },

  /**
   * Create a new product
   */
  async createProduct(input: ProductCreateInput, tenantId: string): Promise<Product> {
    const { data, error } = await supabase
      .from('products')
      .insert({
        ...input,
        tenant_id: tenantId,
        is_active: input.is_active ?? true,
      })
      .select()
      .single();

    if (error) {
      if (error.code === '23505') {
        throw new Error('A product with this SKU already exists');
      }
      throw new Error(`Failed to create product: ${error.message}`);
    }

    return data;
  },

  /**
   * Update an existing product
   */
  async updateProduct(id: string, input: ProductUpdateInput): Promise<Product> {
    const { data, error } = await supabase
      .from('products')
      .update({
        ...input,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update product: ${error.message}`);
    }

    return data;
  },

  /**
   * Delete a product (soft delete by setting is_active to false)
   */
  async deleteProduct(id: string, hardDelete = false): Promise<boolean> {
    if (hardDelete) {
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', id);

      if (error) {
        throw new Error(`Failed to delete product: ${error.message}`);
      }
    } else {
      const { error } = await supabase
        .from('products')
        .update({ is_active: false, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) {
        throw new Error(`Failed to deactivate product: ${error.message}`);
      }
    }

    return true;
  },

  /**
   * Get product categories for filtering
   */
  async getCategories(): Promise<string[]> {
    const { data, error } = await supabase
      .from('products')
      .select('category')
      .not('category', 'is', null);

    if (error) {
      throw new Error(`Failed to fetch categories: ${error.message}`);
    }

    const categories = [...new Set(data?.map(p => p.category).filter(Boolean))] as string[];
    return categories.sort();
  },

  /**
   * Get high-risk products
   */
  async getHighRiskProducts(limit = 20): Promise<Product[]> {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('is_high_risk', true)
      .eq('is_active', true)
      .order('shrinkage_rate', { ascending: false })
      .limit(limit);

    if (error) {
      throw new Error(`Failed to fetch high-risk products: ${error.message}`);
    }

    return data || [];
  },

  /**
   * Bulk update products
   */
  async bulkUpdateProducts(
    ids: string[],
    update: Partial<ProductUpdateInput>
  ): Promise<number> {
    const { data, error } = await supabase
      .from('products')
      .update({
        ...update,
        updated_at: new Date().toISOString(),
      })
      .in('id', ids)
      .select('id');

    if (error) {
      throw new Error(`Failed to bulk update products: ${error.message}`);
    }

    return data?.length || 0;
  },
};

// ============================================
// EMPLOYEE SERVICE
// ============================================

export const employeeService = {
  /**
   * Get all employees with pagination and filtering
   */
  async getEmployees(
    pagination: PaginationParams = {},
    filters: FilterParams = {}
  ): Promise<PaginatedResult<Employee>> {
    const { page = 1, pageSize = 25, sortBy = 'full_name', sortOrder = 'asc' } = pagination;
    const { search, status, store_id } = filters;

    let query = supabase
      .from('employees')
      .select(`
        *,
        store:stores(id, name, code)
      `, { count: 'exact' });

    // Apply filters
    if (search) {
      query = query.or(`full_name.ilike.%${search}%,email.ilike.%${search}%,employee_code.ilike.%${search}%`);
    }
    if (status) {
      query = query.eq('status', status);
    }
    if (store_id) {
      query = query.eq('store_id', store_id);
    }

    // Apply sorting
    query = query.order(sortBy, { ascending: sortOrder === 'asc' });

    // Apply pagination
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    query = query.range(from, to);

    const { data, error, count } = await query;

    if (error) {
      console.error('Error fetching employees:', error);
      throw new Error(`Failed to fetch employees: ${error.message}`);
    }

    return {
      data: data || [],
      total: count || 0,
      page,
      pageSize,
      totalPages: Math.ceil((count || 0) / pageSize),
    };
  },

  /**
   * Get a single employee by ID
   */
  async getEmployee(id: string): Promise<Employee | null> {
    const { data, error } = await supabase
      .from('employees')
      .select(`
        *,
        store:stores(id, name, code)
      `)
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw new Error(`Failed to fetch employee: ${error.message}`);
    }

    return data;
  },

  /**
   * Get employees by store
   */
  async getEmployeesByStore(storeId: string): Promise<Employee[]> {
    const { data, error } = await supabase
      .from('employees')
      .select('*')
      .eq('store_id', storeId)
      .eq('status', 'ACTIVE')
      .order('full_name', { ascending: true });

    if (error) {
      throw new Error(`Failed to fetch employees: ${error.message}`);
    }

    return data || [];
  },

  /**
   * Create a new employee
   */
  async createEmployee(input: EmployeeCreateInput, tenantId: string): Promise<Employee> {
    const { data, error } = await supabase
      .from('employees')
      .insert({
        ...input,
        tenant_id: tenantId,
        status: input.status || 'ACTIVE',
      })
      .select()
      .single();

    if (error) {
      if (error.code === '23505') {
        throw new Error('An employee with this number already exists');
      }
      throw new Error(`Failed to create employee: ${error.message}`);
    }

    return data;
  },

  /**
   * Update an existing employee
   */
  async updateEmployee(id: string, input: EmployeeUpdateInput): Promise<Employee> {
    const { data, error } = await supabase
      .from('employees')
      .update({
        ...input,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update employee: ${error.message}`);
    }

    return data;
  },

  /**
   * Terminate an employee
   */
  async terminateEmployee(id: string, terminationDate?: string): Promise<Employee> {
    const { data, error } = await supabase
      .from('employees')
      .update({
        status: 'TERMINATED',
        termination_date: terminationDate || new Date().toISOString().split('T')[0],
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to terminate employee: ${error.message}`);
    }

    return data;
  },

  /**
   * Delete an employee (hard delete)
   */
  async deleteEmployee(id: string): Promise<boolean> {
    const { error } = await supabase
      .from('employees')
      .delete()
      .eq('id', id);

    if (error) {
      throw new Error(`Failed to delete employee: ${error.message}`);
    }

    return true;
  },

  /**
   * Get employees with high risk scores
   */
  async getHighRiskEmployees(threshold = 70, limit = 20): Promise<Employee[]> {
    const { data, error } = await supabase
      .from('employees')
      .select(`
        *,
        store:stores(id, name, code)
      `)
      .gte('risk_score', threshold)
      .eq('status', 'ACTIVE')
      .order('risk_score', { ascending: false })
      .limit(limit);

    if (error) {
      throw new Error(`Failed to fetch high-risk employees: ${error.message}`);
    }

    return data || [];
  },

  /**
   * Update employee risk score
   */
  async updateRiskScore(id: string, riskScore: number): Promise<void> {
    const { error } = await supabase
      .from('employees')
      .update({
        risk_score: riskScore,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id);

    if (error) {
      throw new Error(`Failed to update employee risk score: ${error.message}`);
    }
  },

  /**
   * Get employee statistics
   */
  async getEmployeeStats(storeId?: string): Promise<{
    total: number;
    active: number;
    avgRiskScore: number;
  }> {
    let query = supabase
      .from('employees')
      .select('status, risk_score');

    if (storeId) {
      query = query.eq('store_id', storeId);
    }

    const { data, error } = await query;

    if (error) {
      throw new Error(`Failed to fetch employee stats: ${error.message}`);
    }

    const employees = data || [];
    const active = employees.filter(e => e.status === 'ACTIVE');
    const riskScores = active.map(e => e.risk_score || 0);

    return {
      total: employees.length,
      active: active.length,
      avgRiskScore: riskScores.length > 0
        ? riskScores.reduce((a, b) => a + b, 0) / riskScores.length
        : 0,
    };
  },
};

// ============================================
// COMBINED EXPORT
// ============================================

export const masterDataService = {
  stores: storeService,
  products: productService,
  employees: employeeService,
};

export default masterDataService;
